import { useMemo } from 'react';

import saltBg from '../assets/salt-bg.jpg';
import { useTranslations } from '../i18n';
import { MENU_CONTENT, MENU_FLIPBOOK_URLS, MENU_ALLERGENS } from '../data/menu';

export function MenuPage() {
  const { t, locale } = useTranslations();

  const sections = MENU_CONTENT[locale].sections;
  const flipbookUrl = MENU_FLIPBOOK_URLS[locale];
  const allergens = MENU_ALLERGENS[locale];
  const labels = {
    weight: t('menuPage.labels.weight', 'Weight') as string,
    allergens: t('menuPage.labels.allergens', 'Allergens') as string,
    price: t('menuPage.labels.price', 'Price') as string,
  };

  const formatters = useMemo(
    () => ({
      bgn: new Intl.NumberFormat(locale === 'bg' ? 'bg-BG' : 'en-GB', {
        style: 'currency',
        currency: 'BGN',
        minimumFractionDigits: 2,
      }),
      eur: new Intl.NumberFormat(locale === 'bg' ? 'bg-BG' : 'en-GB', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
      }),
    }),
    [locale],
  );

  const heading = t('menuPage.heading', 'Main menu') as string;
  const body = t(
    'menuPage.body',
    'Explore our full à la carte selection — salads, starters, mains, desserts, and signature drinks.',
  ) as string;
  const flipbookCta = t('menuPage.flipbookCta', 'Open flipbook') as string;
  const allergenHeading = t('menuPage.allergens.heading', 'Allergen information') as string;
  const allergenBody = t(
    'menuPage.allergens.body',
    'Allergen numbers follow EU Regulation No. 1169/2011. Please notify our team about any allergies before ordering.',
  ) as string;
  const allergenLegendLabel = t('menuPage.allergens.legendLabel', 'Legend') as string;

  const quickLinks: { id: string; label: string }[] = [
    'salads',
    'starters',
    'mains',
    'desserts',
    'wine',
    'alcohol',
    'drinks',
  ]
    .filter((id) => sections.some((section) => section.id === id))
    .map((id) => {
      const section = sections.find((candidate) => candidate.id === id);
      return {
        id,
        label: t(`menuPage.sections.${id}.title`, section?.title ?? id) as string,
      };
    });

  quickLinks.push({ id: 'allergens', label: allergenHeading });

  return (
    <section className="relative isolate overflow-hidden px-6 pb-10 pt-16 sm:px-10 sm:pb-12">
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <img
          src={saltBg}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="h-full w-full object-cover object-center"
        />
        <div
          className="absolute inset-0 bg-[linear-gradient(180deg,rgba(12,16,20,0.9)_0%,rgba(12,16,20,0.88)_100%)]"
          aria-hidden="true"
        />
      </div>

      <div className="relative mx-auto flex max-w-6xl flex-col gap-12">
        <header className="space-y-6 text-center">
          <div className="space-y-4">
            <h1 className="text-4xl font-heading text-[var(--color-text-primary)]">{heading}</h1>
            <p className="mx-auto max-w-3xl text-base text-[var(--color-text-primary)]/75">{body}</p>
          </div>
          <div className="flex justify-center">
            <a
              href={flipbookUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="glass-button glass-button--accent inline-flex items-center justify-center px-6 py-2 text-xs uppercase tracking-[0.18em]"
            >
              {flipbookCta}
            </a>
          </div>
        </header>

        <nav className="mx-auto mt-10 w-full max-w-4xl" aria-label={t('menuPage.heading', 'Main menu') as string}>
          <div className="flex flex-wrap justify-center gap-3 border-t border-white/10 py-4">
            {quickLinks.map((link) => (
              <a
                key={link.id}
                href={`#menu-${link.id}`}
                className="inline-flex items-center justify-center rounded-full border border-white/15 px-4 py-2 text-xs uppercase tracking-[0.28em] text-[var(--color-text-primary)]/80 transition-colors duration-200 hover:bg-[var(--color-accent)] hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgba(194,103,58,0.4)]"
              >
                {link.label}
              </a>
            ))}
          </div>
        </nav>

        <div className="mt-12 divide-y divide-white/10 border-t border-white/10">
          {sections.map((section) => {
            return (
              <article
                key={section.id}
                id={`menu-${section.id}`}
                className="space-y-8 px-4 py-10 scroll-mt-36 md:px-6"
              >
                <div className="space-y-3 text-center md:text-left">
                  <h2 className="text-3xl font-heading uppercase tracking-[0.18em] text-[var(--color-text-primary)]">
                    {section.title}
                  </h2>
                  {section.subtitle ? (
                    <p className="text-sm text-[var(--color-text-primary)]/70">{section.subtitle}</p>
                  ) : null}
                </div>

                {section.items ? (
                  <ul className="grid gap-6 md:grid-cols-2 md:gap-x-10">
                    {section.items.map((item) => (
                      <li key={item.name} className="space-y-3">
                        <div className="flex flex-col gap-2">
                          <div className="flex items-start justify-between gap-4">
                            <div className="space-y-1">
                              <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">
                                {item.name}
                              </h3>
                              {item.description ? (
                                <p className="text-sm text-[var(--color-text-primary)]/70">
                                  {item.description}
                                </p>
                              ) : null}
                            </div>
                            <div className="shrink-0 text-sm text-[var(--color-text-primary)] text-right">
                              <span className="font-semibold">
                                {formatters.bgn.format(item.price.bgn)}
                              </span>
                              <span className="mx-2 text-[var(--color-text-primary)]/50">·</span>
                              <span>{formatters.eur.format(item.price.eur)}</span>
                            </div>
                          </div>

                          <dl className="flex flex-wrap gap-x-6 gap-y-2 text-xs uppercase tracking-[0.18em] text-[var(--color-text-primary)]/50">
                            {item.weight ? (
                              <div className="flex items-center gap-2">
                                <dt>{labels.weight}</dt>
                                <dd className="text-[var(--color-text-primary)]/70">{item.weight}</dd>
                              </div>
                            ) : null}
                            {item.allergens && item.allergens.length > 0 ? (
                              <div className="flex items-center gap-2">
                                <dt>{labels.allergens}</dt>
                                <dd className="text-[var(--color-text-primary)]/70">
                                  {item.allergens.join(', ')}
                                </dd>
                              </div>
                            ) : null}
                          </dl>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : null}

                {section.groups ? (
                  <div className="grid gap-6 md:grid-cols-2 md:gap-x-10">
                    {section.groups.map((group) => (
                      <div key={group.name} className="space-y-3">
                        <h3 className="text-base font-semibold uppercase tracking-[0.24em] text-[var(--color-text-primary)]/70">
                          {group.name}
                        </h3>
                        <ul className="space-y-3">
                          {group.items.map((item) => (
                            <li
                              key={item.name}
                              className="flex flex-col gap-2 border-b border-white/10 pb-3 last:border-none last:pb-0"
                            >
                              <span className="text-sm font-semibold text-[var(--color-text-primary)]">
                                {item.name}
                              </span>
                              <span className="text-sm font-medium text-[var(--color-text-primary)]">
                                {formatters.bgn.format(item.price.bgn)}
                                <span className="mx-2 text-[var(--color-text-primary)]/50">·</span>
                                {formatters.eur.format(item.price.eur)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>

        <footer id="menu-allergens" className="space-y-6 border-t border-white/10 pt-10 scroll-mt-36">
          <div className="space-y-3 text-center md:text-left">
            <h2 className="text-2xl font-heading text-[var(--color-text-primary)]">{allergenHeading}</h2>
            <p className="text-sm text-[var(--color-text-primary)]/70">{allergenBody}</p>
          </div>

          <div className="space-y-4">
            <h3 className="text-xs uppercase tracking-[0.24em] text-[var(--color-text-primary)]/60">
              {allergenLegendLabel}
            </h3>
            <dl className="grid gap-4 text-sm text-[var(--color-text-primary)]/80 md:grid-cols-2">
              {allergens.map((allergen) => (
                <div key={allergen.code} className="flex items-start gap-3">
                  <dt className="font-heading text-lg font-semibold text-[var(--color-text-primary)]">
                    {allergen.code}
                  </dt>
                  <dd>{allergen.name}</dd>
                </div>
              ))}
            </dl>
          </div>
        </footer>
      </div>
    </section>
  );
}
